import React from 'react';

const MessageFormatter = ({ text }) => {
  if (!text || typeof text !== 'string') {
    return <span>{text}</span>;
  }

  const formatMessage = (message) => {
    // Split by double line breaks to separate major sections
    const sections = message.split(/\n\s*\n/);
    
    return sections.map((section, sectionIndex) => {
      if (!section.trim()) return null;
      
      // Check if section is a header (starts with ###, ##, or #)
      if (section.trim().match(/^#{1,3}\s+/)) {
        const headerMatch = section.trim().match(/^(#{1,3})\s+(.+)/);
        if (headerMatch) {
          const level = headerMatch[1].length;
          const text = headerMatch[2];
          const HeaderTag = `h${Math.min(level + 2, 6)}`; // h3, h4, h5, h6
          
          return (
            <div key={sectionIndex} className="message-header">
              <HeaderTag>{text}</HeaderTag>
            </div>
          );
        }
      }
      
      // Process the section for inline formatting and lists
      const lines = section.split('\n');
      const elements = [];
      let currentList = [];
      let listType = null;
      
      lines.forEach((line, lineIndex) => {
        const trimmedLine = line.trim();
        
        if (!trimmedLine) {
          // Empty line - close any open list and add spacing
          if (currentList.length > 0) {
            elements.push(renderList(currentList, listType, elements.length));
            currentList = [];
            listType = null;
          }
          return;
        }
        
        // Check for numbered list (1. 2. etc.)
        const numberedMatch = trimmedLine.match(/^(\d+)\.\s+(.+)/);
        if (numberedMatch) {
          if (listType !== 'ordered') {
            if (currentList.length > 0) {
              elements.push(renderList(currentList, listType, elements.length));
            }
            currentList = [];
            listType = 'ordered';
          }
          currentList.push({
            number: numberedMatch[1],
            text: formatInlineText(numberedMatch[2])
          });
          return;
        }
        
        // Check for bullet list (- or *)
        const bulletMatch = trimmedLine.match(/^[-*]\s+(.+)/);
        if (bulletMatch) {
          if (listType !== 'unordered') {
            if (currentList.length > 0) {
              elements.push(renderList(currentList, listType, elements.length));
            }
            currentList = [];
            listType = 'unordered';
          }
          currentList.push({
            text: formatInlineText(bulletMatch[1])
          });
          return;
        }
        
        // Regular paragraph - close any open list first
        if (currentList.length > 0) {
          elements.push(renderList(currentList, listType, elements.length));
          currentList = [];
          listType = null;
        }
        
        // Check if it's a header line
        if (trimmedLine.match(/^#{1,3}\s+/)) {
          const headerMatch = trimmedLine.match(/^(#{1,3})\s+(.+)/);
          if (headerMatch) {
            const level = headerMatch[1].length;
            const text = headerMatch[2];
            const HeaderTag = `h${Math.min(level + 2, 6)}`;
            
            elements.push(
              <div key={`${sectionIndex}-${lineIndex}`} className="message-header">
                <HeaderTag>{formatInlineText(text)}</HeaderTag>
              </div>
            );
            return;
          }
        }
        
        // Regular paragraph
        elements.push(
          <p key={`${sectionIndex}-${lineIndex}`} className="message-paragraph">
            {formatInlineText(trimmedLine)}
          </p>
        );
      });
      
      // Close any remaining list
      if (currentList.length > 0) {
        elements.push(renderList(currentList, listType, elements.length));
      }
      
      return (
        <div key={sectionIndex} className="message-section">
          {elements}
        </div>
      );
    }).filter(Boolean);
  };
  
  const renderList = (items, type, key) => {
    if (type === 'ordered') {
      return (
        <ol key={key} className="message-ordered-list">
          {items.map((item, index) => (
            <li key={index} className="message-list-item">
              {item.text}
            </li>
          ))}
        </ol>
      );
    } else {
      return (
        <ul key={key} className="message-unordered-list">
          {items.map((item, index) => (
            <li key={index} className="message-list-item">
              {item.text}
            </li>
          ))}
        </ul>
      );
    }
  };
  
  const formatInlineText = (text) => {
    if (!text || typeof text !== 'string') return text || '';
    
    try {
      // Escape any existing HTML to prevent XSS
      let safeText = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      
      // Handle bold text (**text** or __text__)
      safeText = safeText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      safeText = safeText.replace(/__(.*?)__/g, '<strong>$1</strong>');
      
      // Handle italic text (*text* or _text_) - simple version for compatibility
      safeText = safeText.replace(/\*([^*]+)\*/g, '<em>$1</em>');
      safeText = safeText.replace(/_([^_]+)_/g, '<em>$1</em>');
      
      // Handle inline code (`code`)
      safeText = safeText.replace(/`([^`]+)`/g, '<code>$1</code>');
      
      // Handle links [text](url) - basic URL validation
      safeText = safeText.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, linkText, url) => {
        // Basic URL validation
        if (url.match(/^https?:\/\/.+/) || url.match(/^mailto:.+/)) {
          return `<a href="${url}" target="_blank" rel="noopener noreferrer">${linkText}</a>`;
        }
        return match; // Return original if URL doesn't look valid
      });
      
      // Parse the formatted text into React elements
      return parseFormattedText(safeText);
    } catch (error) {
      console.warn('Error formatting text:', error);
      return text; // Return original text if formatting fails
    }
  };
  
  const parseFormattedText = (formattedText) => {
    // Simple regex-based approach for better compatibility
    const elements = [];
    let remaining = formattedText;
    let key = 0;
    
    while (remaining) {
      // Check for links first
      const linkMatch = remaining.match(/^(.*?)<a href="([^"]+)" target="_blank" rel="noopener noreferrer">([^<]+)<\/a>/);
      if (linkMatch) {
        if (linkMatch[1]) {
          elements.push(...parseSimpleFormatting(linkMatch[1], key++));
        }
        elements.push(
          <a 
            key={`link-${key++}`}
            href={linkMatch[2]} 
            target="_blank" 
            rel="noopener noreferrer"
            className="message-link"
          >
            {linkMatch[3]}
          </a>
        );
        remaining = remaining.substring(linkMatch[0].length);
        continue;
      }
      
      // Check for code
      const codeMatch = remaining.match(/^(.*?)<code>([^<]+)<\/code>/);
      if (codeMatch) {
        if (codeMatch[1]) {
          elements.push(...parseSimpleFormatting(codeMatch[1], key++));
        }
        elements.push(<code key={`code-${key++}`} className="inline-code">{codeMatch[2]}</code>);
        remaining = remaining.substring(codeMatch[0].length);
        continue;
      }
      
      // Check for bold
      const boldMatch = remaining.match(/^(.*?)<strong>([^<]+)<\/strong>/);
      if (boldMatch) {
        if (boldMatch[1]) {
          elements.push(...parseSimpleFormatting(boldMatch[1], key++));
        }
        elements.push(<strong key={`strong-${key++}`}>{boldMatch[2]}</strong>);
        remaining = remaining.substring(boldMatch[0].length);
        continue;
      }
      
      // Check for italic
      const italicMatch = remaining.match(/^(.*?)<em>([^<]+)<\/em>/);
      if (italicMatch) {
        if (italicMatch[1]) {
          elements.push(...parseSimpleFormatting(italicMatch[1], key++));
        }
        elements.push(<em key={`em-${key++}`}>{italicMatch[2]}</em>);
        remaining = remaining.substring(italicMatch[0].length);
        continue;
      }
      
      // No more formatting found, add remaining text
      elements.push(...parseSimpleFormatting(remaining, key++));
      break;
    }
    
    return elements.length === 1 && typeof elements[0] === 'string' ? elements[0] : elements;
  };
  
  const parseSimpleFormatting = (text, baseKey) => {
    if (!text) return [];
    
    // Decode HTML entities
    const decodedText = text
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');
    
    return [decodedText];
  };
  
  const formattedContent = formatMessage(text);
  
  return (
    <div className="formatted-message">
      {formattedContent}
    </div>
  );
};

export default MessageFormatter;
